/**
 * ParticleRenderer — renders particles using InstancedMesh with billboard quads.
 *
 * Updates instance matrices, colors, and sizes from ParticleEmitter data.
 * Supports billboard, stretched billboard, and mesh render modes.
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../render/post/mrtOverlay.js';
import type { ParticleEmitter } from './ParticleEmitter.js';
import { PARTICLE_STRIDE, type ParticleConfig } from './types.js';

const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _mat4 = new THREE.Matrix4();
const _color = new THREE.Color();
const _camQuat = new THREE.Quaternion();
const _rotQ = new THREE.Quaternion();
const _zAxis = new THREE.Vector3(0, 0, 1);

export class ParticleRenderer {
  readonly mesh: THREE.InstancedMesh;
  private geometry: THREE.BufferGeometry;
  private material: THREE.Material;
  private maxParticles: number;

  constructor(config: ParticleConfig) {
    this.maxParticles = config.maxParticles;

    // Billboard quad geometry
    this.geometry = new THREE.PlaneGeometry(1, 1);

    // Material setup
    this.material = new THREE.MeshBasicMaterial({
      map: config.texture ?? null,
      transparent: true,
      blending: config.blending ?? THREE.AdditiveBlending,
      depthWrite: config.depthWrite ?? false,
      side: THREE.DoubleSide,
      vertexColors: true,
    });
    markMrtOverlay(this.material); // billboards leave the G-buffer to what is behind them

    this.mesh = new THREE.InstancedMesh(
      this.geometry,
      this.material,
      this.maxParticles,
    );
    // THE DYNAMIC-USAGE LAW (the shader sweep, 2026-10-06): three r185 re-uploads a DynamicDraw attribute on EVERY render call that draws it (each shadow cascade, the scene pass, a reflection) and the first call consumes the update range — later calls sent the WHOLE capacity. Every write here bumps the version, so Static usage uploads once a frame.

    // Instance colors
    const colors = new Float32Array(this.maxParticles * 3);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(colors, 3);

    // Start with 0 visible instances
    this.mesh.count = 0;
    this.mesh.frustumCulled = false; // We handle culling ourselves
  }

  /**
   * Update instance data from emitter.
   */
  update(emitter: ParticleEmitter, camera: THREE.Camera): void {
    const count = emitter.activeCount;
    const data = emitter.data;

    // Get camera quaternion for billboarding
    camera.getWorldQuaternion(_camQuat);

    for (let i = 0; i < count; i++) {
      const o = i * PARTICLE_STRIDE;

      // Position
      _pos.set(data[o], data[o + 1], data[o + 2]);

      // Billboard: face camera
      _quat.copy(_camQuat);

      // Apply per-particle rotation around view axis (reuse module-level scratch)
      if (data[o + 13] !== 0) {
        _rotQ.setFromAxisAngle(_zAxis, data[o + 13]);
        _quat.multiply(_rotQ);
      }

      // Size
      const size = data[o + 8];
      _scale.set(size, size, size);

      // Compose matrix
      _mat4.compose(_pos, _quat, _scale);
      this.mesh.setMatrixAt(i, _mat4);

      // Color — premultiply alpha into RGB because InstancedMesh only
      // supports per-instance RGB (no alpha channel). With additive/
      // premultiplied-alpha blending this produces the correct fade.
      // Clamp to keep defensive against NaN/out-of-range alphas.
      const alpha = Math.max(0, Math.min(1, data[o + 12]));
      _color.setRGB(
        data[o + 9] * alpha,
        data[o + 10] * alpha,
        data[o + 11] * alpha,
      );
      this.mesh.setColorAt(i, _color);
    }

    this.mesh.count = count;
    // Upload only the live instances: needsUpdate alone re-sent the WHOLE capacity (maxParticles × 16 floats +
    // colour) every frame per emitter, even with a handful alive.
    if (count > 0) {
      markLive(this.mesh.instanceMatrix, count * 16);
      if (this.mesh.instanceColor) markLive(this.mesh.instanceColor, count * 3);
    }
  }

  /**
   * Set the particle texture.
   */
  setTexture(texture: THREE.Texture | null): void {
    (this.material as THREE.MeshBasicMaterial).map = texture;
    (this.material as THREE.MeshBasicMaterial).needsUpdate = true;
  }

  /**
   * Set blending mode.
   */
  setBlending(blending: THREE.Blending): void {
    this.material.blending = blending;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}

/** Flag an attribute for upload limited to its first `n` array elements (the live range). */
function markLive(attr: THREE.BufferAttribute, n: number): void {
  attr.clearUpdateRanges();
  attr.addUpdateRange(0, Math.min(n, attr.array.length));
  attr.needsUpdate = true;
}
